import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { surveyService } from '../services/surveyService';
import { SurveyExecutionRequest } from '../types/survey';
import { PanelistFilterBuilder } from './PanelistFilterBuilder';

interface SurveyExecutionFormProps {
  onExecutionStarted?: (executionId: string) => void;
}

export const SurveyExecutionForm: React.FC<SurveyExecutionFormProps> = ({ onExecutionStarted }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [surveyId, setSurveyId] = useState('');
  const [targetCompletes, setTargetCompletes] = useState('10');
  const [surveyUrl, setSurveyUrl] = useState('');
  const [queryParams, setQueryParams] = useState('{"TEST": "YES", "DISABLE_RID_CHECK": "YES"}');
  const [panelistFilters, setPanelistFilters] = useState('{}');
  const [forcedResponses, setForcedResponses] = useState('{}');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    // Validation
    if (!surveyId.trim()) {
      setError('Survey ID is required');
      return;
    }

    const completes = parseInt(targetCompletes);
    if (isNaN(completes) || completes < 1 || completes > 10000) {
      setError('Target completes must be between 1 and 10,000');
      return;
    }

    try {
      setLoading(true);

      // Parse JSON fields
      const request: SurveyExecutionRequest = {
        survey_id: surveyId.trim(),
        target_completes: completes,
      };

      if (surveyUrl.trim()) {
        request.survey_url = surveyUrl.trim();
      }

      if (queryParams.trim()) {
        try {
          request.query_string_params = JSON.parse(queryParams);
        } catch (e) {
          throw new Error('Invalid JSON in query string parameters');
        }
      }

      if (panelistFilters.trim() && panelistFilters.trim() !== '{}') {
        try {
          request.panelist_filters = JSON.parse(panelistFilters);
        } catch (e) {
          throw new Error('Invalid JSON in panelist filters');
        }
      }

      if (forcedResponses.trim() && forcedResponses.trim() !== '{}') {
        try {
          request.forced_responses = JSON.parse(forcedResponses);
        } catch (e) {
          throw new Error('Invalid JSON in forced responses');
        }
      }

      if (additionalInstructions.trim()) {
        request.additional_instructions = additionalInstructions.trim();
      }

      // Execute survey
      const result = await surveyService.executeSurvey(request, token);
      
      setSuccess(`Survey execution started! Execution ID: ${result.execution_id}`);
      
      // Reset form
      setSurveyId('');
      setTargetCompletes('10');
      setSurveyUrl('');
      setQueryParams('{"TEST": "YES", "DISABLE_RID_CHECK": "YES"}');
      setPanelistFilters('{}');
      setForcedResponses('{}');
      setAdditionalInstructions('');

      // Notify parent
      if (onExecutionStarted) {
        onExecutionStarted(result.execution_id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to execute survey');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom color="primary" fontWeight={600}>
        Execute Survey
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure and start a new synthetic survey execution
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Survey ID"
            value={surveyId}
            onChange={(e) => setSurveyId(e.target.value)}
            placeholder="e.g., 12236_SYN"
            disabled={loading}
          />
          <TextField
            fullWidth
            required
            type="number"
            label="Target Completes"
            value={targetCompletes}
            onChange={(e) => setTargetCompletes(e.target.value)}
            inputProps={{ min: 1, max: 10000 }}
            disabled={loading}
          />
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <TextField
              fullWidth
              label="Survey URL (optional)"
              value={surveyUrl}
              onChange={(e) => setSurveyUrl(e.target.value)}
              placeholder="Leave empty to use default Bellomy survey platform"
              disabled={loading}
            />
          </Box>
        </Box>

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Advanced Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Query String Parameters (JSON)"
                value={queryParams}
                onChange={(e) => setQueryParams(e.target.value)}
                disabled={loading}
                helperText='Example: {"TEST": "YES", "DISABLE_RID_CHECK": "YES"}'
              />
              <PanelistFilterBuilder
                value={panelistFilters}
                onChange={setPanelistFilters}
                disabled={loading}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Forced Responses (JSON)"
                value={forcedResponses}
                onChange={(e) => setForcedResponses(e.target.value)}
                disabled={loading}
                helperText='Example: {"Q1": "Strongly Agree"}'
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Instructions"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                disabled={loading}
                placeholder="Any additional instructions for survey completion"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {loading ? 'Starting...' : 'Execute Survey'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SurveyExecutionForm;

